import { z } from "zod";
import { isWeekendDate, TimeSlot } from "@carry/shared";
import { PublicIdSchema } from "./common";

/**
 * F29 業務日報 (取引先別配送連絡表) の Contract。
 * 設計書: docs/08_work_report/01_design.md
 */

/**
 * #627: 請求距離の確認状態。距離突合 (F27) を廃止し、業務日報の明細が状態を持つ。
 * unresolved (未算出) → calculated (算出/補正済み・未確認) → confirmed (確認済み)。
 * 注文ごとの参考距離の状態。請求書の発行ゲートは便の距離 (#668) を見る。
 */
export const BillingDistanceStatus = z.enum(["unresolved", "calculated", "confirmed"]);
/** #627: 請求距離の根拠。osrm = 自動算出値のまま / manual = 担当者が補正した値。 */
export const BillingDistanceSource = z.enum(["osrm", "manual"]);

export const FarePlanSide = z.enum(["billing", "payment"]);
export const FarePlanWriteMode = z.enum(["upsert", "create"]);
export const FareType = z.enum([
  "fixed",
  "distance",
  "area",
  "fixed_distance",
  "fixed_area",
  "distance_area",
  "fixed_distance_area",
]);
export const WorkReportEntrySource = z.enum(["auto", "manual"]);

const MonthSchema = z.string().regex(/^\d{4}-\d{2}$/, {
  message: "month は YYYY-MM 形式であること",
});

// ===== 列レジストリ =====

/**
 * 業務日報の列キー。clients.work_report_config の columns[].key と
 * 一覧テーブル / Excel 出力の双方がこのレジストリを参照する。
 * `derived:` で始まる説明の列は work-report-calculator による計算列 (entry には保存されない)。
 */
export const WORK_REPORT_COLUMNS = {
  work_date: "配達日",
  driver_name: "ドライバー名",
  driver_phone: "ドライバー℡",
  order_number: "受注番号",
  pickup_time: "引取時間",
  delivery_time: "配達時間",
  pickup_name: "集荷先",
  pickup_address: "引取先住所",
  delivery_name: "施設・社名",
  delivery_address: "お届け先住所",
  customer_name: "お客様名",
  payment_method: "支払方法",
  collected_amount: "金額",
  area: "配送地区",
  cargo_name: "積載名",
  cargo_quantity: "個数",
  extra1: "項目①",
  extra2: "項目②",
  billing_distance_km: "請求距離",
  payment_distance_km: "支払距離",
  stopover_count: "立寄件数",
  waiting_minutes: "待機時間(分)",
  billing_fare: "請求用運賃",
  payment_fare: "支払用運賃",
  billing_stopover_fee: "請求用立寄料",
  billing_waiting_fee: "請求用待機料",
  billing_stopover_waiting_total: "請求立寄待機料",
  payment_stopover_fee: "支払用立寄料",
  payment_waiting_fee: "支払用待機料",
  payment_stopover_waiting_total: "支払立寄待機料",
  parking_fee: "駐車料金",
  toll_fee: "高速代",
  /** #616: 高速代・駐車料金に当てはまらない立替 (経費種別 other)。 */
  other_fee: "その他実費",
  // #616 で other_fee を含むようになったため「高速・P代」から改称した。
  toll_parking_total: "実費計",
  payment_toll_parking_fee: "支払用実費計",
  billing_total: "請求合計",
  payment_total: "支払合計",
  remarks: "備考",
} as const;

export const WORK_REPORT_COLUMN_KEYS = Object.keys(
  WORK_REPORT_COLUMNS,
) as readonly (keyof typeof WORK_REPORT_COLUMNS)[];

export const WorkReportColumnKey = z.enum(WORK_REPORT_COLUMN_KEYS as [string, ...string[]]);

/** 列設定未登録の取引先に適用する既定列 */
export const DEFAULT_WORK_REPORT_COLUMN_KEYS: readonly (keyof typeof WORK_REPORT_COLUMNS)[] = [
  "work_date",
  "delivery_time",
  "pickup_time",
  "order_number",
  "pickup_address",
  "delivery_address",
  "driver_name",
  "billing_distance_km",
  "payment_distance_km",
  "stopover_count",
  "waiting_minutes",
  "billing_fare",
  "payment_fare",
  "billing_stopover_waiting_total",
  "payment_stopover_waiting_total",
  "toll_parking_total",
  "billing_total",
  "payment_total",
  "remarks",
];

/** 便区分 (早朝 / 午前 / 午後)。チャーター便契約と行色ルールの判定で共有する */
export const FareShift = z.enum(["early_morning", "morning", "afternoon"]);

/**
 * Excel の塗りつぶし色 (ARGB 8 桁)。現行 Excel が `FFFFFF99` 形式で持つため同形式に揃える。
 * 入力は大文字小文字を問わず受け付け、保存時に大文字へ正規化する。
 */
export const ExcelArgbColor = z
  .string()
  .regex(/^[0-9a-fA-F]{8}$/, "色は ARGB 8 桁の 16 進数で指定してください")
  .transform((value) => value.toUpperCase());

/** 行色ルールの判定種別。値の一致で行全体を塗る */
export const WorkReportRowColorMatchKind = z.enum([
  "special_rate_key",
  "extra1",
  "extra2",
  "shift",
]);

export const WorkReportRowColorRule = z.object({
  match: z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("special_rate_key"),
      value: z.string().min(1),
    }),
    z.object({ kind: z.literal("extra1"), value: z.string().min(1) }),
    z.object({ kind: z.literal("extra2"), value: z.string().min(1) }),
    /** 便区分。判定は配達時刻優先・なければ引取時刻 (work-report-calculator の resolveFareShift と同一) */
    z.object({ kind: z.literal("shift"), value: FareShift }),
  ]),
  color: ExcelArgbColor,
});

export const WorkReportColumnConfig = z.object({
  key: WorkReportColumnKey,
  /** 取引先固有ラベル。省略時は WORK_REPORT_COLUMNS の既定ラベル */
  label: z.string().min(1).optional(),
  /** Excel データ行の背景色 (ARGB)。行色ルールが一致した行では行色が優先される */
  fill: ExcelArgbColor.optional(),
});

export const WorkReportConfig = z.object({
  columns: z.array(WorkReportColumnConfig).min(1),
  /** Excel 月シート末尾の取引先名セル (現行 Excel 互換)。省略時は取引先名 */
  sheet_title: z.string().optional(),
  /** 行単位の色分けルール。上から評価し、最初に一致したルールの色を行全体へ適用する */
  row_color_rules: z.array(WorkReportRowColorRule).optional(),
});

/** 一覧・Excel に渡す解決済み列定義 (label 解決後) */
export const ResolvedWorkReportColumn = z.object({
  key: WorkReportColumnKey,
  label: z.string(),
  /** Excel データ行の背景色 (ARGB)。未設定は塗らない */
  fill: ExcelArgbColor.optional(),
});

// ===== 料金プラン =====

export const FareSpecialRate = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  amount: z.number().int(),
});

export const FareDistanceRate = z.object({
  km: z.number().int().min(0),
  amount: z.number().int(),
});

export const FareAreaRate = z.object({
  area_name: z.string().min(1),
  amount: z.number().int(),
});

/**
 * 定額運賃 (チャーター便・特殊運賃) の立寄料・待機料を定額に含めるかの既定値 (#650)。
 * 既定は「含む」= #638 の挙動。フラグを持たない既存プランもこの値で解釈する。
 */
export const FLAT_RATE_INCLUDES_STOPOVER_WAITING_FEE_DEFAULT = true;

export const FareCharterContract = z.object({
  shift: FareShift,
  unit_price: z.number().int().min(0),
  contracted_count: z.number().int().min(0),
});

export const FarePlan = z.object({
  public_id: PublicIdSchema,
  client_public_id: PublicIdSchema,
  side: FarePlanSide,
  fare_type: FareType,
  fixed_fare_amount: z.number().int().min(0).nullable(),
  charter_contracts: z.array(FareCharterContract),
  /**
   * 定額運賃 (チャーター枠に充当された明細 / 特殊運賃) の立寄料・待機料を定額に含めるか (#650)。
   * true = 定額に含む (0 円)、false = 従来どおり別途加算。フラグを持たない既存プランは既定で解釈する。
   */
  flat_rate_includes_stopover_waiting_fee: z.boolean(),
  stopover_unit_price: z.number().int(),
  waiting_unit_minutes: z.number().int(),
  waiting_unit_price: z.number().int(),
  special_rates: z.array(FareSpecialRate),
  effective_from: z.iso.date(),
  effective_to: z.iso.date().nullable(),
  distance_rates: z.array(FareDistanceRate),
  area_rates: z.array(FareAreaRate),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

/**
 * 料金プランの作成/更新 (rates 全置換)。
 * 既定は (client, side, effective_from) が既存と一致する場合は更新、なければ新規作成。
 * write_mode=create は新しい適用日の追加用で、同一日付の既存プランがある場合は保存前に拒否する。
 * 対象取引先はルートパラメータ publicId で指定する。
 */
export const UpsertFarePlanInput = z
  .object({
    write_mode: FarePlanWriteMode.optional(),
    side: FarePlanSide,
    fare_type: FareType,
    fixed_fare_amount: z.number().int().min(0).nullable().optional(),
    charter_contracts: z.array(FareCharterContract).default([]),
    /** #650: 省略時は `FLAT_RATE_INCLUDES_STOPOVER_WAITING_FEE_DEFAULT` として保存する */
    flat_rate_includes_stopover_waiting_fee: z.boolean().optional(),
    stopover_unit_price: z.number().int().min(0).default(0),
    waiting_unit_minutes: z.number().int().min(0).default(15),
    waiting_unit_price: z.number().int().min(0).default(0),
    special_rates: z.array(FareSpecialRate).default([]),
    effective_from: z.iso.date(),
    effective_to: z.iso.date().nullable().optional(),
    distance_rates: z.array(FareDistanceRate).default([]),
    area_rates: z.array(FareAreaRate).default([]),
  })
  .superRefine((input, ctx) => {
    const hasDistanceRates = input.distance_rates.length > 0;
    const hasAreaRates = input.area_rates.length > 0;
    const usesDistance =
      input.fare_type === "distance" ||
      input.fare_type === "fixed_distance" ||
      input.fare_type === "distance_area" ||
      input.fare_type === "fixed_distance_area";
    const usesArea =
      input.fare_type === "area" ||
      input.fare_type === "fixed_area" ||
      input.fare_type === "distance_area" ||
      input.fare_type === "fixed_distance_area";
    const usesFixed =
      input.fare_type === "fixed" ||
      input.fare_type === "fixed_distance" ||
      input.fare_type === "fixed_area" ||
      input.fare_type === "fixed_distance_area";
    const hasCharterContracts = input.charter_contracts.some((c) => c.contracted_count > 0);
    const hasSpecialRates = input.special_rates.length > 0;
    if (usesFixed && input.fixed_fare_amount == null && !hasCharterContracts && !hasSpecialRates) {
      ctx.addIssue({
        code: "custom",
        path: ["fixed_fare_amount"],
        message: "チャーター便契約、固定料金、特殊運賃のいずれかを入力してください",
      });
    }
    if (!usesDistance && hasDistanceRates) {
      ctx.addIssue({
        code: "custom",
        path: ["distance_rates"],
        message: "距離変動以外では距離運賃表を設定できません",
      });
    }
    if (!usesArea && hasAreaRates) {
      ctx.addIssue({
        code: "custom",
        path: ["area_rates"],
        message: "エリア変動以外ではエリア運賃表を設定できません",
      });
    }
  });

// ===== 明細 =====

/** work-report-calculator による計算列 */
export const WorkReportEntryAmounts = z.object({
  billing_stopover_fee: z.number().int(),
  billing_waiting_fee: z.number().int(),
  payment_stopover_fee: z.number().int(),
  payment_waiting_fee: z.number().int(),
  billing_expense: z.number().int(),
  payment_expense: z.number().int(),
  billing_total: z.number().int(),
  payment_total: z.number().int(),
});

export const WorkReportAreaResolutionStatus = z.enum([
  "resolved",
  "no_candidates",
  "no_match",
  "ambiguous",
]);

export const WorkReportAreaResolutionCandidate = z.object({
  area_name: z.string(),
  billing_amount: z.number().int().nullable(),
  payment_amount: z.number().int().nullable(),
  match_type: z.enum(["exact", "address", "none"]),
});

export const WorkReportAreaResolution = z.object({
  status: WorkReportAreaResolutionStatus,
  reason_code: WorkReportAreaResolutionStatus,
  candidates: z.array(WorkReportAreaResolutionCandidate),
});

/**
 * #645: 明細が属する便 (請求単位 = レーン × 取引先 × 便区分 × 特殊運賃種別)。
 *
 * 請求距離・支払距離・運賃は**便に 1 本**だけ立ち、`is_representative` の行に載る。
 * 注文ごとの距離 (`billing_distance_km`) は参考値として残るので 0 にはならない。
 */
export const WorkReportEntryTrip = z.object({
  /** 便の public_id。距離をまだ算出していない便は null (便自体は明細から決まる) */
  public_id: PublicIdSchema.nullable(),
  route_public_id: PublicIdSchema,
  /** 配達時刻から導出した便区分。時刻が読めない便は null */
  time_slot: TimeSlot.nullable(),
  special_rate_key: z.string().nullable(),
  /** 便の請求距離 (その取引先の地点だけを訪問順に経由)。未算出は null */
  billing_distance_km: z.number().nullable(),
  /** 便の支払距離。OSRM 算出では請求距離と同じ値 (#766)。未算出は null */
  payment_distance_km: z.number().nullable(),
  /** OSRM の自動算出値 (支払)。補正しても原本として残る (#642) */
  payment_distance_osrm_km: z.number().nullable(),
  /** 支払距離の根拠。未算出の便は null */
  payment_distance_source: BillingDistanceSource.nullable(),
  /** 支払距離の補正理由。source=manual のとき入る */
  payment_distance_adjustment_reason: z.string().nullable(),
  /** OSRM の自動算出値。補正しても原本として残る (#667) */
  billing_distance_osrm_km: z.number().nullable(),
  /** 請求距離の根拠。未算出の便は null */
  billing_distance_source: BillingDistanceSource.nullable(),
  /** 補正理由。source=manual のとき入る */
  billing_distance_adjustment_reason: z.string().nullable(),
  /**
   * 便の構成 (所属明細) が保存時と食い違っている。
   * true の間は再算出するまで請求書を発行できない (#670 / #668)。
   */
  distance_stale: z.boolean(),
  /** 距離・運賃を載せる代表行か */
  is_representative: z.boolean(),
  /** 便に属する明細数 */
  entry_count: z.number().int(),
  /** 便の距離を算出できなかった理由 */
  distance_warning: z.string().nullable(),
});

export const WorkReportEntry = z.object({
  public_id: PublicIdSchema,
  client_public_id: PublicIdSchema,
  work_date: z.iso.date(),
  order_public_id: PublicIdSchema.nullable(),
  route_public_id: PublicIdSchema.nullable(),
  /**
   * #645: 所属する便。**null は便が未確定**であることを意味する。
   * 自動生成行なら配車前 (請求距離を決められないので運賃も出さない)、
   * 手動追加行なら便を持たず従来どおり明細単位で計算する行。
   */
  trip: WorkReportEntryTrip.nullable(),
  driver_public_id: PublicIdSchema.nullable(),
  driver_name: z.string().nullable(),
  driver_phone: z.string().nullable(),
  order_number: z.string().nullable(),
  pickup_time: z.string().nullable(),
  delivery_time: z.string().nullable(),
  pickup_name: z.string().nullable(),
  pickup_address: z.string().nullable(),
  delivery_name: z.string().nullable(),
  delivery_address: z.string().nullable(),
  customer_name: z.string().nullable(),
  payment_method: z.string().nullable(),
  collected_amount: z.number().int().nullable(),
  area: z.string().nullable(),
  cargo_name: z.string().nullable(),
  cargo_quantity: z.number().nullable(),
  extra1: z.string().nullable(),
  extra2: z.string().nullable(),
  /**
   * #627: 請求距離 (集荷→配達の直行距離)。距離突合 (F27) の廃止で、算出値・補正値・確定値を
   * この 1 列に集約した。値の確からしさは billing_distance_status が持つ。未算出は null。
   */
  billing_distance_km: z.number().nullable(),
  billing_distance_status: BillingDistanceStatus,
  /** OSRM の自動算出値。補正しても原本として残るので補正量を追える */
  billing_distance_osrm_km: z.number().nullable(),
  billing_distance_source: BillingDistanceSource.nullable(),
  /** 算出できなかった理由 (座標欠落・OSRM 失敗)。担当者が補正で解消する */
  billing_distance_warning: z.string().nullable(),
  billing_distance_confirmed_at: z.iso.datetime({ offset: true }).nullable(),
  /** 確認者。内部 ID は返さず表示名だけを公開する */
  billing_distance_confirmed_by_name: z.string().nullable(),
  billing_distance_adjustment_reason: z.string().nullable(),
  payment_distance_km: z.number().nullable(),
  stopover_count: z.number().int(),
  /** 実訪問から導出した値。根拠不足時は null。 */
  auto_stopover_count: z.number().int().nullable(),
  /** 有効な立寄件数の由来。 */
  stopover_count_source: z.enum(["auto", "manual", "needs_review"]),
  /** 自動値を手動補正した理由。 */
  stopover_count_override_reason: z.string().nullable(),
  waiting_minutes: z.number().int(),
  special_rate_key: z.string().nullable(),
  billing_fare: z.number().int().nullable(),
  billing_fare_manual: z.boolean(),
  payment_fare: z.number().int().nullable(),
  payment_fare_manual: z.boolean(),
  parking_fee: z.number().int(),
  toll_fee: z.number().int(),
  /** #616: 高速代・駐車料金に当てはまらない立替 (経費種別 other) の実費。 */
  other_fee: z.number().int(),
  payment_toll_parking_fee: z.number().int().nullable(),
  /** #616: true の間は注文紐付け経費からの再取込で実費 4 列を上書きしない (手修正の保護)。 */
  expense_manual: z.boolean(),
  remarks: z.string().nullable(),
  source: WorkReportEntrySource,
  area_resolution: WorkReportAreaResolution,
  billing_area_blocking: z.boolean(),
  amounts: WorkReportEntryAmounts,
  warnings: z.array(z.string()),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

const entryEditableFields = {
  driver_public_id: PublicIdSchema.nullable().optional(),
  order_number: z.string().nullable().optional(),
  pickup_time: z.string().nullable().optional(),
  delivery_time: z.string().nullable().optional(),
  pickup_name: z.string().nullable().optional(),
  pickup_address: z.string().nullable().optional(),
  delivery_name: z.string().nullable().optional(),
  delivery_address: z.string().nullable().optional(),
  customer_name: z.string().nullable().optional(),
  payment_method: z.string().nullable().optional(),
  collected_amount: z.number().int().nullable().optional(),
  area: z.string().nullable().optional(),
  cargo_name: z.string().nullable().optional(),
  cargo_quantity: z.number().nullable().optional(),
  extra1: z.string().nullable().optional(),
  extra2: z.string().nullable().optional(),
  /**
   * #627: 請求距離を直接入力できるのは手動追加行だけ。自動生成行は OSRM 自動値を原本に持ち
   * 請求書ゲートの対象になるため、理由なしで確定距離を差し替えられると監査が抜ける。
   * 自動生成行への指定は updateEntryDistance (理由必須) へ誘導し、API 側で拒否する。
   */
  billing_distance_km: z.number().min(0).nullable().optional(),
  /** 支払距離は #617 の実走行ベースで請求根拠ではないため、行の種別によらず手入力できる。 */
  payment_distance_km: z.number().min(0).nullable().optional(),
  stopover_count: z.number().int().min(0).optional(),
  stopover_count_override_reason: z.string().trim().min(1).max(500).nullable().optional(),
  waiting_minutes: z.number().int().min(0).optional(),
  special_rate_key: z.string().nullable().optional(),
  /**
   * 運賃の手動上書き。number を渡すと manual フラグ ON (再計算で上書きされない)、
   * null を渡すと manual フラグ OFF (自動計算に戻す)。
   */
  billing_fare: z.number().int().nullable().optional(),
  payment_fare: z.number().int().nullable().optional(),
  /**
   * #616: 実費 (高速代・駐車料金・その他)。値を渡すと手修正とみなし expense_manual が ON になり、
   * 以降は経費からの再取込で上書きされない。自動追従に戻すには expense_manual: false を渡す。
   */
  parking_fee: z.number().int().min(0).optional(),
  toll_fee: z.number().int().min(0).optional(),
  other_fee: z.number().int().min(0).optional(),
  payment_toll_parking_fee: z.number().int().min(0).nullable().optional(),
  expense_manual: z.boolean().optional(),
  remarks: z.string().nullable().optional(),
};

export const CreateWorkReportEntryInput = z.object({
  client_public_id: PublicIdSchema,
  work_date: z.iso.date(),
  ...entryEditableFields,
});

export const UpdateWorkReportEntryInput = z.object({
  publicId: PublicIdSchema,
  work_date: z.iso.date().optional(),
  ...entryEditableFields,
});

// ===== 一覧 / 生成 =====

export const WorkReportSummary = z.object({
  entry_count: z.number().int(),
  billing_fare_total: z.number().int(),
  payment_fare_total: z.number().int(),
  billing_stopover_fee_total: z.number().int(),
  billing_waiting_fee_total: z.number().int(),
  payment_stopover_fee_total: z.number().int(),
  payment_waiting_fee_total: z.number().int(),
  billing_expense_total: z.number().int(),
  payment_expense_total: z.number().int(),
  billing_grand_total: z.number().int(),
  payment_grand_total: z.number().int(),
  /**
   * #666 / #708: チャーター契約枠のうち、稼働便が契約台数に届かなかった台数と金額
   * (請求側 / 支払側)。数える対象日は**稼働日** (平日かつ休業日に登録されていない日)。
   * #708 以降この額は**請求に計上済み**で、その日の最も金額の低い充当便へ上乗せされている。
   * 内訳を月次画面へ提示するための値 (充当先の便が 1 つも無い日の分だけは計上されない)。
   */
  billing_charter_shortfall_count: z.number().int(),
  billing_charter_shortfall_amount: z.number().int(),
  payment_charter_shortfall_count: z.number().int(),
  payment_charter_shortfall_amount: z.number().int(),
});

export const ListWorkReportInput = z.object({
  client_public_id: PublicIdSchema,
  month: MonthSchema,
});

export const ListWorkReportOutput = z.object({
  entries: z.array(WorkReportEntry),
  summary: WorkReportSummary,
  /** 解決済み列定義 (取引先設定 or 既定列セット) */
  columns: z.array(ResolvedWorkReportColumn),
  /** 対象月初日時点で有効なプラン (UI の特殊運賃選択肢・警告表示用)。未設定は null */
  billing_plan: FarePlan.nullable(),
  payment_plan: FarePlan.nullable(),
});

export const GenerateWorkReportInput = z.object({
  client_public_id: PublicIdSchema,
  month: MonthSchema,
});

export const GenerateWorkReportOutput = z.object({
  /** 新規作成された行数 */
  created: z.number().int(),
  /** 既存行があるためスキップされた注文数 */
  skipped: z.number().int(),
  warnings: z.array(z.string()),
});

/**
 * #616: 注文単位で登録された経費 (高速代・駐車料金) を既存の明細行の実費欄へ再取込する。
 * ETC のように後日金額が確定する経費を、生成済みの業務日報へ反映するための操作。
 */
export const SyncWorkReportExpensesInput = z.object({
  client_public_id: PublicIdSchema,
  month: MonthSchema,
});

export const SyncWorkReportExpensesOutput = z.object({
  /** 実費が変わって更新された行数 */
  updated: z.number().int(),
  /** 手修正済み (expense_manual) のため上書きしなかった行数 */
  skipped_manual: z.number().int(),
});

// ===== 請求距離の算出 / 補正 / 確認 (Issue #627) =====

/**
 * #627: 距離突合 (F27) を廃止し、注文別請求距離の算出・補正・確認を業務日報へ移した。
 * 対象は取引先 × 月。entry_public_ids を渡すと明細を絞れる (省略時は月内の対象行すべて)。
 */
const distanceOperationTarget = {
  client_public_id: PublicIdSchema,
  month: MonthSchema,
  entry_public_ids: z.array(PublicIdSchema).min(1).max(500).optional(),
};

export const CalculateWorkReportDistancesInput = z.object(distanceOperationTarget);

export const ConfirmWorkReportDistancesInput = z.object(distanceOperationTarget);

/** 明細単位の警告。算出・確認できなかった行を担当者に返す。 */
export const WorkReportDistanceWarning = z
  .object({
    entry_public_id: PublicIdSchema,
    order_number: z.string().nullable(),
    delivery_name: z.string().nullable(),
    message: z.string().min(1).max(500),
  })
  .strict();

/** #645: 便単位の警告。距離を算出できなかった便を担当者に返す。 */
export const WorkReportTripDistanceWarning = z
  .object({
    trip_key: z.string(),
    route_public_id: PublicIdSchema,
    time_slot: TimeSlot.nullable(),
    work_date: z.iso.date(),
    message: z.string().min(1).max(500),
  })
  .strict();

export const CalculateWorkReportDistancesOutput = z
  .object({
    /** OSRM 距離を算出できた明細数 (注文ごとの参考距離) */
    calculated: z.number().int(),
    /** 座標欠落・OSRM 失敗で未算出のままの明細数 */
    unresolved: z.number().int(),
    /** 確認済みのため再算出しなかった明細数 */
    skipped_confirmed: z.number().int(),
    warnings: z.array(WorkReportDistanceWarning),
    /** #645: 請求に使う便の距離を算出できた便数 */
    trips_calculated: z.number().int(),
    /** #645: 距離を算出できなかった便数 (この便の運賃は出ない) */
    trips_unresolved: z.number().int(),
    trip_warnings: z.array(WorkReportTripDistanceWarning),
  })
  .strict();

export const ConfirmWorkReportDistancesOutput = z
  .object({
    /** 確認済みにした明細数 */
    confirmed: z.number().int(),
    /** 距離が無く確認できなかった明細数 */
    unresolved: z.number().int(),
    warnings: z.array(WorkReportDistanceWarning),
  })
  .strict();

/** 請求距離の補正。監査のため理由を必須にする。 */
export const UpdateWorkReportEntryDistanceInput = z
  .object({
    publicId: PublicIdSchema,
    billing_distance_km: z.number().min(0),
    adjustment_reason: z.string().trim().min(1).max(500),
  })
  .strict();

/** #667 / #642: 便の請求距離・支払距離の補正。明細の参考距離ではなく、運賃に効く便の距離を動かす。 */
export const UpdateWorkReportTripDistanceInput = z
  .object({
    publicId: PublicIdSchema,
    billing_distance_km: z.number().min(0).optional(),
    payment_distance_km: z.number().min(0).optional(),
    adjustment_reason: z.string().trim().min(1).max(500),
  })
  .strict()
  .refine((v) => v.billing_distance_km !== undefined || v.payment_distance_km !== undefined, {
    message: "billing_distance_km または payment_distance_km のいずれかが必要です",
  });

export const UpdateWorkReportConfigInput = z.object({
  publicId: PublicIdSchema,
  config: WorkReportConfig.nullable(),
});

export const GetWorkReportConfigOutput = z.object({
  /** 保存されている生設定 (未設定は null) */
  config: WorkReportConfig.nullable(),
  /** 解決済み列定義 (既定列セット込み) */
  columns: z.array(ResolvedWorkReportColumn),
});

export type FarePlanDto = z.infer<typeof FarePlan>;
export type FareSpecialRateDto = z.infer<typeof FareSpecialRate>;
export type FareDistanceRateDto = z.infer<typeof FareDistanceRate>;
export type FareAreaRateDto = z.infer<typeof FareAreaRate>;
export type FareShiftDto = z.infer<typeof FareShift>;
export type FareCharterContractDto = z.infer<typeof FareCharterContract>;
export type UpsertFarePlanInputDto = z.infer<typeof UpsertFarePlanInput>;
export type WorkReportConfigDto = z.infer<typeof WorkReportConfig>;
export type WorkReportColumnConfigDto = z.infer<typeof WorkReportColumnConfig>;
export type WorkReportRowColorRuleDto = z.infer<typeof WorkReportRowColorRule>;
export type WorkReportRowColorMatchKindDto = z.infer<typeof WorkReportRowColorMatchKind>;
export type ResolvedWorkReportColumnDto = z.infer<typeof ResolvedWorkReportColumn>;
export type WorkReportEntryDto = z.infer<typeof WorkReportEntry>;

/**
 * #645: 明細行に出す請求距離。距離は便に 1 本なので**代表行にだけ**載せ、同じ便の他の行は
 * null にする (現行 Excel の「2 件目以降は 0km」と同じ見え方)。便に属さない行 (配車前・手動追加)
 * は注文ごとの距離をそのまま出す。
 *
 * DB の `billing_distance_km` は注文ごとの参考値として残す。ここで分けるのは
 * 「保持する値」と「請求として出す値」であって、参考値を消すわけではない。
 *
 * **画面・Excel・請求書がこの 1 本を共有する**こと。片方だけ規則を変えると、同じ便に
 * 対して違う距離が表示され、どれが請求根拠か分からなくなる。
 */
export function workReportBillingDistanceKm(entry: WorkReportEntryDto): number | null {
  if (!entry.trip) return entry.billing_distance_km;
  return entry.trip.is_representative ? entry.trip.billing_distance_km : null;
}

/** #645: 明細行に出す支払距離。請求距離と同じく便の代表行にだけ載せる。 */
export function workReportPaymentDistanceKm(entry: WorkReportEntryDto): number | null {
  if (!entry.trip) return entry.payment_distance_km;
  return entry.trip.is_representative ? entry.trip.payment_distance_km : null;
}

// ===== 取引先の休業日 (#708) =====

/**
 * 取引先の休業日 (Issue #708)。チャーター便契約の定額枠は**稼働日にだけ**配る。
 *
 * **稼働日 = 平日 (月〜金) かつ この一覧に無い日**。稼働日は案件数によらず契約台数を満額請求し、
 * 不足分はその日の最も金額の低い充当便へ上乗せする。休業日は定額を立てず距離/エリア運賃に回す。
 *
 * 土日は曜日で判定するので登録しない。祝日・GW・お盆・年末年始は**休業日として登録する**
 * (祝日マスタは持たない。Issue #708 の顧客回答で確定)。
 */
export const ClientClosedDay = z.object({
  closed_date: z.iso.date(),
  /** 休業の理由 (GW・お盆・年末年始・祝日など)。表示専用で計算には使わない */
  reason: z.string().max(100).nullable(),
});

export const ListClientClosedDaysInput = z.object({
  publicId: PublicIdSchema,
  /** 省略時は登録済みの全休業日。指定するとその月だけ */
  month: MonthSchema.optional(),
});

/**
 * 月単位の全置換。`days` に無い日は解除される。
 * 画面が「その月のカレンダー」で編集するので、差分ではなく月の確定値を送らせる
 * (差分だと解除の取りこぼしが分からない)。
 */
export const ReplaceClientClosedDaysInput = z
  .object({
    publicId: PublicIdSchema,
    month: MonthSchema,
    days: z.array(ClientClosedDay).default([]),
  })
  .superRefine((input, ctx) => {
    const seen = new Set<string>();
    for (const [index, day] of input.days.entries()) {
      // 対象月の外を受けると、保存しても月単位の読み出しに現れず「消えた」ように見える
      if (!day.closed_date.startsWith(`${input.month}-`)) {
        ctx.addIssue({
          code: "custom",
          path: ["days", index, "closed_date"],
          message: `休業日 ${day.closed_date} が対象月 ${input.month} の範囲外です`,
        });
      }
      // 土日は曜日で判定するので登録しない。受け入れると画面のチェックボックスは常に
      // オフ・操作不可なのにレコードだけ残り、解除できない行き止まりになる
      if (isWeekendDate(day.closed_date)) {
        ctx.addIssue({
          code: "custom",
          path: ["days", index, "closed_date"],
          message: `土日 (${day.closed_date}) は登録不要です。曜日から自動で休業日として扱います`,
        });
      }
      if (seen.has(day.closed_date)) {
        ctx.addIssue({
          code: "custom",
          path: ["days", index, "closed_date"],
          message: `休業日 ${day.closed_date} が重複しています`,
        });
      }
      seen.add(day.closed_date);
    }
  });

export type ClientClosedDayDto = z.infer<typeof ClientClosedDay>;
export type ListClientClosedDaysInputDto = z.infer<typeof ListClientClosedDaysInput>;
export type ReplaceClientClosedDaysInputDto = z.infer<typeof ReplaceClientClosedDaysInput>;

export type WorkReportEntryTripDto = z.infer<typeof WorkReportEntryTrip>;
export type WorkReportAreaResolutionDto = z.infer<typeof WorkReportAreaResolution>;
export type WorkReportEntryAmountsDto = z.infer<typeof WorkReportEntryAmounts>;
export type CreateWorkReportEntryInputDto = z.infer<typeof CreateWorkReportEntryInput>;
export type UpdateWorkReportEntryInputDto = z.infer<typeof UpdateWorkReportEntryInput>;
export type WorkReportSummaryDto = z.infer<typeof WorkReportSummary>;
export type ListWorkReportInputDto = z.infer<typeof ListWorkReportInput>;
export type ListWorkReportOutputDto = z.infer<typeof ListWorkReportOutput>;
export type GenerateWorkReportOutputDto = z.infer<typeof GenerateWorkReportOutput>;
export type SyncWorkReportExpensesInputDto = z.infer<typeof SyncWorkReportExpensesInput>;
export type SyncWorkReportExpensesOutputDto = z.infer<typeof SyncWorkReportExpensesOutput>;
export type WorkReportColumnKeyDto = z.infer<typeof WorkReportColumnKey>;
export type BillingDistanceStatusDto = z.infer<typeof BillingDistanceStatus>;
export type BillingDistanceSourceDto = z.infer<typeof BillingDistanceSource>;
export type WorkReportDistanceWarningDto = z.infer<typeof WorkReportDistanceWarning>;
export type CalculateWorkReportDistancesInputDto = z.infer<
  typeof CalculateWorkReportDistancesInput
>;
export type CalculateWorkReportDistancesOutputDto = z.infer<
  typeof CalculateWorkReportDistancesOutput
>;
export type ConfirmWorkReportDistancesInputDto = z.infer<typeof ConfirmWorkReportDistancesInput>;
export type ConfirmWorkReportDistancesOutputDto = z.infer<typeof ConfirmWorkReportDistancesOutput>;
export type UpdateWorkReportEntryDistanceInputDto = z.infer<
  typeof UpdateWorkReportEntryDistanceInput
>;
export type UpdateWorkReportTripDistanceInputDto = z.infer<
  typeof UpdateWorkReportTripDistanceInput
>;
